import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Save } from 'lucide-react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { TextField } from '@/components/text-field';
import { TextAreaField } from '@/components/textarea-field';
import { errorHandler } from '@/lib/utils';
import { useSaveAsTemplate } from '../hooks/use-save-as-template';
export function SaveAsTemplateDialog({ workspaceId, sourceCampaignId, defaultName, locked, open: openProp, onOpenChange, }) {
    const { t } = useTranslation('campaign');
    const isControlled = openProp !== undefined;
    const [internalOpen, setInternalOpen] = React.useState(false);
    const open = isControlled ? openProp : internalOpen;
    const setOpen = React.useCallback((next) => {
        if (!isControlled)
            setInternalOpen(next);
        onOpenChange?.(next);
    }, [isControlled, onOpenChange]);
    const form = useForm({
        defaultValues: { name: defaultName ?? '', description: '' },
    });
    const { mutate, isPending } = useSaveAsTemplate();
    React.useEffect(() => {
        if (open) {
            form.reset({ name: defaultName ?? '', description: '' });
        }
    }, [open, defaultName, form]);
    const onSubmit = (values) => {
        mutate({
            workspaceId,
            sourceCampaignId,
            name: values.name.trim(),
            description: values.description?.trim() || undefined,
        }, {
            onSuccess: (data) => {
                toast.success(data.message);
                setOpen(false);
            },
            onError: errorHandler,
        });
    };
    return (<Dialog open={open} onOpenChange={setOpen}>
            {isControlled ? null : (<DialogTrigger asChild>
                    <Button variant="outline" size="sm" disabled={locked} className="gap-2">
                        <Save className="h-4 w-4"/>
                        {t('save_as_template.button')}
                    </Button>
                </DialogTrigger>)}
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('save_as_template.title')}</DialogTitle>
                    <DialogDescription>{t('save_as_template.description')}</DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <div className="space-y-4">
                        <TextField control={form.control} name="name" label={t('save_as_template.name_label')} placeholder={t('save_as_template.name_placeholder')} required autoFocus disabled={isPending} maxLength={120}/>
                        <TextAreaField control={form.control} name="description" label={t('save_as_template.description_label')} placeholder={t('save_as_template.description_placeholder')} disabled={isPending} maxLength={2000}/>
                    </div>
                </Form>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" disabled={isPending}>
                            {t('save_as_template.cancel')}
                        </Button>
                    </DialogClose>
                    <Button onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
                        {isPending ? t('save_as_template.saving') : t('save_as_template.save')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
